"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Target, FileCode2, Award, TrendingUp, ArrowRight, CircleCheck as CheckCircle2, Sparkles, Bot, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const processSteps = [{
  step: "01",
  id: "audit",
  stageNumber: "Stage 1",
  title: "AI Search Visibility Audit",
  iconName: Search,
  summary: "A stakeholder call on your business model, commercial priorities and target locations, followed by sampling how your brand appears across AI tools.",
  details: ["Sample AI answers across ChatGPT, Google AI Overviews and AI Mode, Perplexity and Copilot with prompts real buyers would ask", "Baseline screenshots, prompt logs, initial content review and technical crawlability checks", "Sampled brand appearances, cited sources, competitor mentions and crawl or rendering checks"],
  deliverable: "Concise findings document with prioritised risks and quick wins"
}, {
  step: "02",
  id: "research",
  stageNumber: "Stage 2",
  title: "Search Intent and Competitor Research",
  iconName: Target,
  summary: "Mapping buyer journeys into search queries and AI prompts across awareness, consideration and decision stages.",
  details: ["Map buyer journeys across awareness, consideration and decision queries", "Review competitor content and sources that earn citations or strong organic rankings", "Identify high-intent questions, topic gaps and formats best suited for answer engines"],
  deliverable: "AI search opportunity map of terms, questions and formats"
}, {
  step: "03",
  id: "optimisation",
  stageNumber: "Stage 3",
  title: "Technical and Content Optimisation",
  iconName: FileCode2,
  summary: "Prioritising technical fixes and key content rewrites or additions so your pages are accessible and easy for AI systems to read.",
  details: ["Technical fixes covering crawl access, robots.txt, rendering and clean structured data markup", "Key content rewrites to answer core buyer questions early in clear, unambiguous paragraphs", "Work implemented by your developers and writers with my guidance, or supported hands-on"],
  deliverable: "Clear implementation notes or tickets for developers and writers"
}, {
  step: "04",
  id: "authority",
  stageNumber: "Stage 4",
  title: "Brand Authority and Discoverability",
  iconName: Award,
  summary: "Strengthening entity consistency across the web and refining your About, profile and service details to aid discoverability.",
  details: ["Entity consistency across primary directories, business profiles and the website", "Digital PR opportunities and review strategy tailored to realistic budgets, prioritising quality over volume", "Refining About and service pages so systems understand who you serve and what you provide"],
  deliverable: "Entity and authority action plan"
}, {
  step: "05",
  id: "monitoring",
  stageNumber: "Stage 5",
  title: "Monitoring and Refinement",
  iconName: TrendingUp,
  summary: "Repeating prompt tests at regular intervals and combining directional observations with your existing analytics where possible.",
  details: ["Repeat prompt tests at regular intervals across major AI answer engines", "Simple dashboards combined with existing web analytics and referral data where available", "Directional reporting highlighting concrete examples, observed trends and business impact"],
  deliverable: "Regular visibility reports and refinement priorities"
}];
export default function Aireadiness() {
  const [activeStep, setActiveStep] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const ActiveIcon = processSteps[activeStep].iconName;
  return <section id="aireadinessprocess" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-0 right-1/4 -z-10 size-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="absolute bottom-10 left-10 -z-10 size-80 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <Badge variant="outline" className="mb-4 py-1 px-3 border-primary/20 text-primary bg-primary/5 rounded-full text-xs font-semibold tracking-wide uppercase">
            <Sparkles className="size-3.5 mr-1.5 text-accent" />
            Process
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground tracking-tight mb-6">
            My Five-Stage AI Search Optimisation Process
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            My process is flexible and adapted to your scope, budget and current SEO maturity. No fixed rankings, citations or timelines are promised; the focus is on clear, methodical improvements that support both AI discovery and traditional search.
          </p>
        </div>

        {/* Desktop Step Selectors */}
        <div className="hidden md:grid grid-cols-5 gap-2 lg:gap-4 mb-12 border-b border-border pb-6">
          {processSteps.map((item, index) => {
          const IconComponent = item.iconName;
          const isActive = activeStep === index;
          return <Button key={item.id} data-index={index} variant="ghost" onClick={() => setActiveStep(index)} className={`flex flex-col items-start p-4 h-auto rounded-xl transition-all text-left justify-start ${isActive ? "bg-card shadow-md border border-border/80 text-foreground" : "hover:bg-muted/60 text-muted-foreground"}`}>
                <div className="flex items-center justify-between w-full mb-3">
                  <span className={`text-xs font-bold font-mono px-2 py-0.5 rounded-md ${isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {item.step}
                  </span>
                  <IconComponent className={`size-5 ${isActive ? "text-primary" : "text-muted-foreground/60"}`} />
                </div>
                <span className="text-sm font-semibold line-clamp-2 leading-snug">
                  {item.title}
                </span>
              </Button>;
        })}
        </div>

        {/* Active Stage Detail + Right-Hand Card */}
        <div className="grid lg:grid-cols-12 gap-8 items-stretch mb-16">
          <Card className="lg:col-span-7 bg-card border-border shadow-lg flex flex-col justify-between">
            <CardContent className="p-6 sm:p-8 flex flex-col h-full justify-between">
              <div>
                <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-primary/10 text-primary">
                      <ActiveIcon className="size-6" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-accent uppercase tracking-wider block">
                        {processSteps[activeStep].stageNumber}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Step {processSteps[activeStep].step} of 05
                      </span>
                    </div>
                  </div>

                  {/* Mobile Step Buttons */}
                  <div className="flex items-center gap-1.5 md:hidden">
                    {processSteps.map((_, idx) => <Button key={idx} data-index={idx} size="icon" variant={activeStep === idx ? "default" : "outline"} onClick={() => setActiveStep(idx)} className="size-7 text-xs rounded-full">
                        {idx + 1}
                      </Button>)}
                  </div>
                </div>

                <motion.div key={activeStep} initial={shouldReduceMotion ? {
                opacity: 1
              } : {
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3
              }}>
                  <h3 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-4">
                    {processSteps[activeStep].title}
                  </h3>

                  <p className="text-base text-muted-foreground leading-relaxed mb-6 font-sans">
                    {processSteps[activeStep].summary}
                  </p>

                  <div className="space-y-3 mb-8">
                    <span className="text-xs font-semibold text-foreground uppercase tracking-wider font-sans block">
                      Key Actions:
                    </span>
                    <ul className="space-y-2.5">
                      {processSteps[activeStep].details.map((detail, dIdx) => <li key={dIdx} data-index={dIdx} className="flex items-start gap-3 text-sm text-foreground/90 font-sans">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{detail}</span>
                        </li>)}
                    </ul>
                  </div>
                </motion.div>
              </div>

              <div className="pt-6 border-t border-border mt-auto">
                <div className="p-4 rounded-lg bg-muted/50 border border-border/50 flex items-center gap-3">
                  <Bot className="size-5 text-primary shrink-0" />
                  <div className="text-xs sm:text-sm">
                    <span className="font-semibold text-foreground block">
                      Deliverable:
                    </span>
                    <span className="text-muted-foreground">
                      {processSteps[activeStep].deliverable}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right-hand Card */}
          <Card className="lg:col-span-5 bg-primary text-primary-foreground shadow-lg flex flex-col justify-between relative overflow-hidden">
            <div className="absolute -bottom-10 -right-10 size-60 bg-accent/20 rounded-full blur-2xl" />
            <CardContent className="p-6 sm:p-8 flex flex-col h-full justify-between relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-foreground/10 text-primary-foreground text-xs font-medium mb-6">
                  <Bot className="size-3.5" />
                  Consultancy Standards
                </div>

                <h3 className="text-2xl font-serif font-bold text-primary-foreground mb-4 leading-tight">
                  How I work through each stage
                </h3>

                <p className="text-primary-foreground/80 text-sm sm:text-base leading-relaxed mb-6 font-sans">
                  Every step is designed to give you actionable clarity and practical priorities rather than confusing technical jargon.
                </p>

                <div className="space-y-3 pt-4 border-t border-primary-foreground/15 mb-6">
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-primary-foreground/90">
                    <div className="size-2 rounded-full bg-accent shrink-0" />
                    <span>Plain-English reporting that explains what the findings mean for your business</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-primary-foreground/90">
                    <div className="size-2 rounded-full bg-accent shrink-0" />
                    <span>Recommendations tied to your commercial goals and available internal capacity</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs sm:text-sm text-primary-foreground/90">
                    <div className="size-2 rounded-full bg-accent shrink-0" />
                    <span>No guaranteed rankings or citations — realistic, evidence-based guidance</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <Button asChild size="lg" className="w-full bg-background text-foreground hover:bg-background/90 font-semibold shadow-sm">
                  <Link to="/ContactPage">
                    <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                    Book a Free AI Consultation
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent font-medium">
                  <Link to="/CaseStudiesPage">
                    Explore Case Studies
                    <ArrowRight className="size-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Step Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {processSteps.map((s, idx) => {
          const Icon = s.iconName;
          return <Card key={s.id} data-index={idx} className={`border transition-all cursor-pointer ${activeStep === idx ? "border-primary bg-primary/5 shadow-sm" : "border-border/60 bg-card hover:border-border"}`} onClick={() => setActiveStep(idx)}>
                <CardContent className="p-4 flex flex-col justify-between h-full">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-mono font-bold text-muted-foreground">
                      {s.step}
                    </span>
                    <Icon className={`size-4 ${activeStep === idx ? "text-primary" : "text-muted-foreground/60"}`} />
                  </div>
                  <span className="text-xs font-semibold text-foreground line-clamp-2">
                    {s.title}
                  </span>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}